"use client";

import { useEffect, useState, useRef } from "react";
import { useInstantNav } from "@/hooks/useInstantNav"

import { useCartStore } from "@/store/cart";
import { useCartCalculations } from "@/hooks/useCartCalculations";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { useLocaleStore } from "@/store/locale";
import { useBusinessStore } from "@/store/business";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import { useCustomerAccounts } from "@/hooks/useCustomerAccounts";
import { useLocaleRedirect } from "@/hooks/useLocaleRedirect";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { getMultipleProducts } from "@/lib/api/business-api";
import { CartItem } from "@/components/cart/CartItem";
import { CartSummary } from "@/components/cart/CartSummary";
import { EmptyCart } from "@/components/cart/EmptyCart";
import { useAnalytics } from "@/components/AnalyticsProvider";

export function CartPageContent() {
  const navigate = useInstantNav()
  const { t } = usePageTranslation();
  const { items, clearCart } = useCartStore();
  const appliedCoupon = useCartStore((s) => s.appliedCoupon);
  const setAppliedCoupon = useCartStore((s) => s.setAppliedCoupon);
  const clearAppliedCoupon = useCartStore((s) => s.clearAppliedCoupon);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [removedItems, setRemovedItems] = useState<string[]>([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const hasSynced = useRef(false);
  const analytics = useAnalytics();
  // A store whose cart IS the checkout form never shows this page. Only once
  // the setting has come from the API: a persisted value may be another visit's.
  const { skipCart, isLoaded: orderingLoaded } = useCustomerAccounts();
  const skippingCart = orderingLoaded && skipCart;
  useLocaleRedirect(skippingCart, "/checkout");

  // Track checkout step: view_cart
  useEffect(() => {
    analytics?.trackCheckoutStep('view_cart');
  }, [analytics]);

  // Wait for zustand store to rehydrate from localStorage
  useEffect(() => {
    // The StoreRehydration component calls rehydrate() on mount.
    // We need to wait for the cart store to finish rehydrating.
    const unsub = useCartStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });
    // If already rehydrated (e.g. navigated client-side), check immediately
    if (useCartStore.persist.hasHydrated()) {
      setIsHydrated(true);
    }
    return () => { unsub(); };
  }, []);
  const currentCurrency = useLocaleStore((state) => state.currentCurrency);
  const currentBusiness = useBusinessStore((state) => state.currentBusiness);
  // The business's own icon wins — the locale store's cookie default is BDT,
  // which mislabels every non-Bangladeshi tenant's prices.
  const currencyIcon =
    (currentBusiness as { currencyIcon?: string } | null)?.currencyIcon ||
    getCurrencySymbol(currentCurrency);
  const settingsBusinessId = useBusinessSettings((state) => state.businessId);
  // Fallback: read businessId from localStorage if Zustand stores haven't hydrated yet on this route.
  const [lsBusinessId, setLsBusinessId] = useState<string>("");
  // (build-id: coupon-fix-1)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("business-settings-storage-v2");
      const parsed = raw ? JSON.parse(raw) : null;
      const id = parsed?.state?.businessId || "";
      if (id) setLsBusinessId(id);
    } catch {
      /* ignore */
    }
  }, []);
  const businessId =
    (currentBusiness as any)?.id ||
    (currentBusiness as any)?.businessId ||
    settingsBusinessId ||
    lsBusinessId ||
    "";

  // Sync cart items with server data on mount (after hydration)
  useEffect(() => {
    if (!isHydrated || hasSynced.current || items.length === 0 || !businessId) return;
    hasSynced.current = true;

    const syncCart = async () => {
      setIsSyncing(true);
      try {
        const slugs = [...new Set(items.map((item) => item.slug))];
        const productsMap = await getMultipleProducts(slugs, businessId);

        const removed: string[] = [];
        const store = useCartStore.getState();

        for (const item of items) {
          const productData = productsMap[item.slug];
          if (!productData?.product) {
            // Product no longer exists — remove from cart
            removed.push(item.name);
            store.removeItem(item.id, item.variantId);
            continue;
          }

          const product = productData.product;

          // Find variant if applicable
          const variant = item.variantId && product.variants
            ? product.variants.find((v) => v.id === item.variantId)
            : null;

          const price = variant?.price || product.price;
          const currentPrice = price.discounted ?? price.original;
          const originalPrice = price.discounted ? price.original : undefined;
          const image = variant?.media?.thumbnail || variant?.media?.gallery?.[0]?.url || product.media?.thumbnail || product.media?.gallery?.[0]?.url || item.image;
          const name = product.name;
          const stock = variant?.availability?.stock ?? product.availability?.stock;
          const vatPercentage = variant?.vat != null
            ? Number(variant.vat)
            : product.vatPercentage ?? (product.vat != null ? Number(product.vat) : undefined);
          const weight = variant?.weight?.value ?? product.weight?.value;

          // Check if anything changed
          const needsUpdate =
            item.price !== currentPrice ||
            item.originalPrice !== originalPrice ||
            item.name !== name ||
            item.image !== image ||
            (stock != null && item.stock !== stock) ||
            (vatPercentage != null && item.vatPercentage !== vatPercentage) ||
            (weight != null && item.weight !== weight);

          if (needsUpdate) {
            // Update the item in the store by removing and re-adding with correct data
            const updatedItem = {
              ...item,
              price: currentPrice,
              originalPrice,
              name,
              image,
              stock: stock ?? item.stock,
              maxQuantity: stock ?? item.maxQuantity,
              vatPercentage: vatPercentage ?? item.vatPercentage,
              weight: weight ?? item.weight,
            };

            // If quantity exceeds new stock, adjust
            if (stock != null && item.quantity > stock) {
              if (stock === 0) {
                removed.push(`${item.name} (${t("product.outOfStock")})`);
                store.removeItem(item.id, item.variantId);
                continue;
              }
              updatedItem.quantity = stock;
            }

            // Direct state update for the item
            useCartStore.setState((state) => {
              const updatedItems = state.items.map((i) =>
                i.id === item.id && i.variantId === item.variantId
                  ? { ...i, ...updatedItem }
                  : i
              );
              return {
                items: updatedItems,
                subtotal: updatedItems.reduce((sum, i) => sum + i.price * i.quantity, 0),
                itemCount: updatedItems.reduce((sum, i) => sum + i.quantity, 0),
                totalWeight: updatedItems.reduce((sum, i) => sum + (i.weight || 0) * i.quantity, 0),
              };
            });
          }
        }

        if (removed.length > 0) {
          setRemovedItems(removed);
        }
      } catch (error) {
        console.error("Failed to sync cart with server:", error);
      } finally {
        setIsSyncing(false);
      }
    };

    syncCart();
  }, [isHydrated, businessId]);

  // Calculate cart totals — coupon discount is always a server-computed fixed amount
  const calculations = useCartCalculations({
    taxRate: 0, // 0% tax by default, can be configured
    shippingFee: 0, // Will be calculated at checkout
    freeShippingThreshold: 0, // Can be configured per business
    couponDiscount: appliedCoupon?.discount || 0,
    couponType: "fixed",
    currencyIcon: currencyIcon,
  });

  // If items change (qty/removal), clear applied coupon — server may now reject it
  useEffect(() => {
    if (!appliedCoupon) return;
    // Only clear if coupon was set and cart is empty now
    if (items.length === 0) {
      clearAppliedCoupon();
    }
  }, [items.length, appliedCoupon, clearAppliedCoupon]);

  const handleCheckout = () => {
    // Navigate to checkout page
    navigate("/checkout");
  };

  const handleContinueShopping = () => {
    // Navigate to home page or products page
    navigate("/");
  };

  // Show loading state until store is rehydrated from localStorage, and in
  // place of the cart while it is being skipped
  if (!isHydrated || skippingCart) {
    return (
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 flex items-center justify-center min-h-[40vh]">
        <div className="flex items-center gap-2 text-brand-muted">
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {t(skippingCart ? "checkout.loading" : "cart.loadingCart")}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return <EmptyCart onContinueShopping={handleContinueShopping} />;
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
      <div className="flex items-center justify-between mb-3 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-brand-fg">{t("cart.title")}</h1>
        {!showClearConfirm ? (
          <button
            onClick={() => setShowClearConfirm(true)}
            className="text-sm text-brand-danger hover:text-brand-danger/80 transition-colors flex items-center gap-1"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t("cart.clearCart")}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-brand-muted">{t("cart.clearConfirm")}</span>
            <button
              onClick={() => { clearCart(); setShowClearConfirm(false); }}
              className="text-sm px-3 py-1 bg-brand-danger text-brand-danger-foreground rounded-brand-sm hover:bg-brand-danger/90 transition-colors"
            >
              {t("common.yes")}
            </button>
            <button
              onClick={() => setShowClearConfirm(false)}
              className="text-sm px-3 py-1 bg-brand-border text-brand-fg rounded-brand-sm hover:opacity-80 transition-opacity"
            >
              {t("common.no")}
            </button>
          </div>
        )}
      </div>

      {/* Syncing indicator */}
      {isSyncing && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {t("cart.syncing")}
        </div>
      )}

      {/* Removed items notice */}
      {removedItems.length > 0 && (
        <div className="mb-3 rounded-brand border border-brand-warning/30 bg-brand-warning/10 p-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-brand-fg">{t("cart.itemsUpdated")}</p>
              <ul className="mt-1 text-xs text-brand-muted">
                {removedItems.map((name, i) => (
                  <li key={i}>• {t("cart.itemRemoved", { name })}</li>
                ))}
              </ul>
            </div>
            <button onClick={() => setRemovedItems([])} className="text-brand-muted/70 hover:text-brand-fg">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-2 sm:space-y-4">
          {items.map((item) => (
            <CartItem
              key={`${item.id}-${item.variantId || 'default'}`}
              item={item}
              currencyIcon={currencyIcon}
            />
          ))}
        </div>

        {/* Cart Summary */}
        <div className="lg:col-span-1">
          <CartSummary
            calculations={calculations}
            onCheckout={handleCheckout}
            businessId={businessId}
            productIds={items.map((i) => i.productId)}
            appliedCoupon={appliedCoupon}
            onCouponApplied={setAppliedCoupon}
            onCouponRemoved={clearAppliedCoupon}
          />
        </div>
      </div>
    </div>
  );
}
